/**
 * Nado gateway error type and the codes the SDK branches on.
 *
 * @see {@link https://docs.nado.xyz/developer-resources/api/errors | Nado error codes (full table)}
 */

/**
 * Codes are shared across all Nado executes and queries, not just
 * withdrawals — keep this module flow-agnostic.
 */
export const NADO_ERROR_CODES = {
  /** Amount would open a borrow with `spot_leverage: "false"`. */
  SPOT_EXECUTE_EXCEEDS_BORROW_LIMIT: 2017,
  /** Gateway-level nonce rejection. A consumed `tx_nonce` reverts in the EVM
   * instead — see `EVM_REVERT`. */
  INVALID_NONCE: 2022,
  /** Subaccount has never deposited, so it doesn't exist on Nado yet. */
  NO_PRIOR_DEPOSIT: 2024,
  /** Withdrawal tx not found at the given submission index. */
  INVALID_WITHDRAWAL_IDX: 3004,
  /** Fast-withdrawal validator signatures not aggregated yet. */
  NOT_ENOUGH_FAST_WITHDRAWAL_SIGNATURES: 3005,
  /** Protocol 24h withdrawal limit exceeded. */
  WITHDRAW_RISK: 4004,
  /** Catch-all EVM revert — the reason lives in the message, not the code.
   * A consumed `tx_nonce` lands here (see `parseNadoExpectedNonce`). */
  EVM_REVERT: 4003,
} as const

export type NadoErrorCode =
  (typeof NADO_ERROR_CODES)[keyof typeof NADO_ERROR_CODES]

/**
 * Stamped at the throw site — retry logic must never infer the failure kind
 * from which fields happen to be missing.
 * - `api`: Nado answered `{status:'failure'}` (or a malformed success)
 * - `http`: non-2xx response
 * - `timeout`: our 30s backstop fired — the caller already waited
 * - `transport`: request never completed (network drop, garbled body)
 */
export type NadoErrorKind = 'api' | 'http' | 'timeout' | 'transport'

/** A failed Nado gateway call, classified by `kind`. */
export class NadoGatewayError extends Error {
  readonly kind: NadoErrorKind
  /** Nado's application error code — set only for `api`-kind failures. */
  readonly errorCode: number | undefined
  /** HTTP status, kept for diagnostics (present on `http` and bad-JSON 2xx). */
  readonly httpStatus: number | undefined

  constructor(
    message: string,
    options: { kind: NadoErrorKind; errorCode?: number; httpStatus?: number },
  ) {
    super(message)
    this.name = 'NadoGatewayError'
    this.kind = options.kind
    this.errorCode = options.errorCode
    this.httpStatus = options.httpStatus
  }
}

/**
 * A submit went unanswered and its replay didn't settle the question — the
 * withdrawal may or may not have landed. Distinct from a plain failure because
 * the user must not be told to retry: a fresh nonce would withdraw twice.
 */
export class NadoWithdrawalUnconfirmedError extends Error {
  constructor(message = 'Nado withdrawal outcome could not be confirmed') {
    super(message)
    this.name = 'NadoWithdrawalUnconfirmedError'
  }
}

/** Request never completed — safe to retry. Excludes timeouts: a retry after
 * a 30s stall would hold the user another 30s. */
export function isNadoTransportError(error: unknown): boolean {
  return error instanceof NadoGatewayError && error.kind === 'transport'
}

/** Maps a gateway failure response to a NadoGatewayError. */
export function createNadoGatewayError(
  response: { error?: string; error_code?: number },
  fallbackMessage: string,
): NadoGatewayError {
  return new NadoGatewayError(response.error ?? fallbackMessage, {
    kind: 'api',
    errorCode: response.error_code,
  })
}

export function isNadoErrorCode(error: unknown, code: NadoErrorCode): boolean {
  return error instanceof NadoGatewayError && error.errorCode === code
}

/**
 * `EVM_REVERT` is a catch-all, so the nonce case is only identifiable from the
 * revert reason: `Reverted with: "Invalid nonce: expected: 41"`. Verified
 * against the prod gateway.
 *
 * Returns the nonce Nado wants next, or undefined if this isn't a nonce
 * revert — callers must treat undefined as "unknown outcome", never as
 * "didn't happen", since the wording is Nado's to change.
 */
const NADO_EXPECTED_NONCE_PATTERN = /Invalid nonce: expected:\s*(\d+)/

export function parseNadoExpectedNonce(error: unknown): bigint | undefined {
  if (
    !(error instanceof NadoGatewayError) ||
    error.errorCode !== NADO_ERROR_CODES.EVM_REVERT
  ) {
    return undefined
  }
  const captured = error.message.match(NADO_EXPECTED_NONCE_PATTERN)?.[1]
  if (captured === undefined) {
    return undefined
  }
  return BigInt(captured)
}
